/**
 * Cross-app token handoff helpers used by AppSwitcher.
 *
 * When two product frontends live on different origins (e.g. localhost:44000 and
 * localhost:44100, or stock.bill-app.fr and staff.bill-app.fr), localStorage is
 * not shared. To avoid forcing the user to log in again, the source app appends
 * the JWT pair as URL params and the destination app picks them up on mount.
 */

const TOKEN_PARAM = 't';
const REFRESH_PARAM = 'r';

/**
 * Build a URL with the current accessToken/refreshToken appended as query params.
 * Returns the original URL unchanged if either token is missing.
 */
export function appendAuthHandoff(url: string): string {
  const token = localStorage.getItem('accessToken');
  const refresh = localStorage.getItem('refreshToken');
  if (!token || !refresh) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}${TOKEN_PARAM}=${encodeURIComponent(token)}&${REFRESH_PARAM}=${encodeURIComponent(refresh)}`;
}

/**
 * Reads the handoff tokens from the current location, stores them in
 * localStorage, then strips them from the URL (history.replaceState).
 * Should be called as early as possible in the app bootstrap (main.tsx).
 */
export function consumeAuthHandoff(): boolean {
  if (typeof window === 'undefined') return false;
  const url = new URL(window.location.href);
  const token = url.searchParams.get(TOKEN_PARAM);
  const refresh = url.searchParams.get(REFRESH_PARAM);
  if (!token || !refresh) return false;

  localStorage.setItem('accessToken', token);
  localStorage.setItem('refreshToken', refresh);

  url.searchParams.delete(TOKEN_PARAM);
  url.searchParams.delete(REFRESH_PARAM);
  window.history.replaceState({}, '', url.toString());
  return true;
}
